/** @jsxImportSource @emotion/react */
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { useLoadWorkspaceFromUrl } from '@app/components/Risk/Workspaces/hooks/useLoadWorkspaceFromUrl'
import { Add } from '@mui/icons-material'
import { Box, Button, Modal, Tooltip, useTheme } from '@mui/material'
import { RiskMapView } from '@app/components/Molecules/MapView'
import { sideDrawerContent } from '@app/components/Molecules/RiskSideDrawerContent/RiskSideDrawerContent.styles'
import { InformativePanel } from '@app/components/Molecules/RiskSideDrawerContent/tabs'
import { SideDrawer } from '@app/components/Molecules/SideDrawer'
import { MapboxOptions } from 'mapbox-gl'
import { useEffect, useState } from 'react'
import { riskPageContainer, sideDrawerContainer, sideDrawerFab } from './RiskPage.styles'
import { Outlet } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { isAuthenticatedAtom } from '@app/state/authAtom'
import { infoLayerFocusAtom } from '@app/state/layerAtom'
import { createPortal } from 'react-dom'
import { LayerImportDialog } from '@app/components/Molecules/MapView/RiskMapView/LayerImportDialog'

export type ViewOrientation = Pick<
  MapboxOptions,
  'center' | 'zoom' | 'pitch' | 'maxPitch' | 'zoom' | 'bearing'
>
export type riskSideDrawerTab = 'Risk' | 'Stories' | 'Welcome & FAQ'

export const RiskPage = () => {
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(true)
  const [activeTab, setActiveTab] = useState<riskSideDrawerTab | null>(null)
  const showAddLayerButton = activeTab !== 'Stories'
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const infoLayerFocus = useAtomValue(infoLayerFocusAtom)
  const sideDrawerOverrideWidth = infoLayerFocus === 'risk_table' ? 1149 : undefined

  const mapContext = useMap()
  const { setMapPadding, flyTo, map, setShowAddLayersModalTab, getMapPadding } = mapContext
  const [showImportDialog, setShowImportDialog] = useState(false)

  useLoadWorkspaceFromUrl()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'S') {
        e.preventDefault()
        setShowAddLayersModalTab('element')
      }
      if (e.ctrlKey && e.shiftKey && e.key === 'H') {
        e.preventDefault()
        setShowAddLayersModalTab('hazard')
      }
      if (e.ctrlKey && e.shiftKey && e.key === 'I') {
        e.preventDefault()
        setShowAddLayersModalTab('contextual')
      }
      if (e.ctrlKey && e.shiftKey && e.key === 'U') {
        e.preventDefault()
        setShowImportDialog((prev) => !prev)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [setShowAddLayersModalTab])

  return (
    <>
      <Box css={riskPageContainer} id="riskPageContainer">
        <Box css={sideDrawerContainer}>
          <SideDrawer
            isOpen={isAuthenticated && isOpen}
            mapContext={mapContext}
            handleToggle={() => setIsOpen(!isOpen)}
            side="left"
            initialWidth={600}
            overrideWidth={sideDrawerOverrideWidth}
            tabIcons={[]}
            tabs={[]}
            tab={activeTab ?? undefined}
            setTab={(newTab) => {
              if (newTab !== activeTab) setActiveTab(newTab as riskSideDrawerTab)
            }}
            handleWidthChange={(width: number) => {
              const { left: previousWidth } = getMapPadding(true)
              setMapPadding({ top: 0, left: width, bottom: 0, right: 0 })
              if (map && width !== previousWidth) {
                flyTo({
                  latitude: map.getCenter().lat,
                  longitude: map.getCenter().lng,
                  zoom: map.getZoom(),
                  usePadding: true,
                  duration: 800,
                })
              }
            }}
          >
            <InformativePanel css={sideDrawerContent} />
            {showAddLayerButton && (
              <Tooltip title="Add layers" placement="right">
                <Button
                  css={sideDrawerFab({ theme })}
                  size="small"
                  onClick={() => setShowAddLayersModalTab('elements')}
                  aria-label="Add layers"
                  className="tutorial-add-layers-button"
                >
                  <Add fontSize="medium" />
                </Button>
              </Tooltip>
            )}
          </SideDrawer>
        </Box>

        <RiskMapView />
        {showImportDialog &&
          createPortal(
            <Modal open={showImportDialog} onClose={() => setShowImportDialog(false)}>
              <LayerImportDialog onClose={() => setShowImportDialog(false)} />
            </Modal>,
            document.body,
          )}
      </Box>
      <Outlet />
    </>
  )
}
